import gql from 'graphql-tag';

const GET_CLIENTS_FOR_DROPDOWN = gql`
  query($listInfo: ListInfoInput!) {
    getClientList(listInfo: $listInfo) {
      clients {
        accountId
        name
      }
      listInfo {
        hasMore
      }
    }
  }
`;

const GET_BASIC_CLIENT_DATA = gql`
  query($listInfo: ListInfoInput!) {
    getClientList(listInfo: $listInfo) {
      clients {
        accountId
        name
        stage
        status
        logoFileName
      }
      listInfo {
        hasMore
      }
    }
  }
`;

const GET_CLIENTS = gql`
  query($listInfo: ListInfoInput!) {
    getClientList(listInfo: $listInfo) {
      clients {
        accountId
        name
        primaryContact {
          name
          firstName
          lastName
          email
          userId
        }
        secondaryContact {
          name
          firstName
          lastName
          email
          userId
        }
        accountManager {
          name
          firstName
          lastName
          email
        }
        stage
        status
        accountType
        logoFileName
        customFields
        policyMigrationStatus
      }
      listInfo {
        hasMore
      }
    }
  }
`;

const GET_SITE_LIST = gql`
  query($input: ListInfoInput!) {
    getClientSiteList(input: $input) {
      sites {
        id
        name
        businessHour {
          day
          start
          end
        }
        holidayList {
          id
          name
        }
        timezoneCode
        working24x7
        contactNumber
        account {
          accountId
          name
        }
        hq
        installerInfo {
          id
          installerName
          installerDownloadUrl
        }
        address {
          addressId
          name
          line1
          line2
          line3
          city
          postalCode
          countryCode
          stateCode
          type
        }
      }
      listInfo {
        page
        pageSize
        condition
        totalCount
        hasMore
      }
    }
  }
`;

const GET_CLIENTS_FOR_POLICY_MIGRATION = gql`
  query($listInfo: ListInfoInput!) {
    getClientList(listInfo: $listInfo) {
      clients {
        accountId
        name
        policyMigrationStatus
      }
      listInfo {
        hasMore
      }
    }
  }
`;

const GET_CLIENT_DETAILS = gql`
  query clientDetails($accountId: ID!) {
    getClientDetails(accountId: $accountId) {
      accountId
      name
      primaryContact {
        firstName
        lastName
        email
        phoneNumber
        contactNumber
        countryCode
        contactType
      }
      secondaryContact {
        firstName
        lastName
        email
        phoneNumber
        contactNumber
        countryCode
        contactType
      }
      technicianGroups {
        groupId
        name
        description
      }
      emailDomainList
    }
  }
`;

const CREATE_CLIENT = gql`
  mutation(
    $name: String
    $stage: String
    $status: String
    $logoFileName: String
    $emailDomains: [String]
    $primaryContact: UserInput
    $secondaryContact: UserInput
    $accountManager: UserInput
    $technicianGroups: [GroupInput]
    $site: SiteInput
    $customFields: JSON
  ) {
    createClient(
      input: {
        name: $name
        stage: $stage
        status: $status
        logoFileName: $logoFileName
        emailDomains: $emailDomains
        primaryContact: $primaryContact
        secondaryContact: $secondaryContact
        accountManager: $accountManager
        technicianGroups: $technicianGroups
        site: $site
        customFields: $customFields
      }
    ) {
      accountId
      name
      secondaryContact {
        name
        firstName
      }
      hqSite {
        id
        name
      }
      primaryContact {
        name
        email
        userId
        firstName
      }
      technicianGroups {
        name
        groupId
      }
    }
  }
`;

const CREATE_CLIENT_WITH_POLICY = gql`
  mutation(
    $name: String
    $stage: String
    $status: String
    $logoFileName: String
    $emailDomains: [String]
    $primaryContact: UserInput
    $secondaryContact: UserInput
    $accountManager: UserInput
    $technicianGroups: [GroupInput]
    $site: SiteInput
    $customFields: JSON
    $addPolicySetMapping: JSON
    $updatePolicySetMapping: JSON
    $deletePolicySetMapping: JSON
  ) {
    createClient(
      input: {
        name: $name
        stage: $stage
        status: $status
        logoFileName: $logoFileName
        emailDomains: $emailDomains
        primaryContact: $primaryContact
        secondaryContact: $secondaryContact
        accountManager: $accountManager
        technicianGroups: $technicianGroups
        site: $site
        customFields: $customFields
        addPolicySetMapping: $addPolicySetMapping
        updatePolicySetMapping: $updatePolicySetMapping
        deletePolicySetMapping: $deletePolicySetMapping
      }
    ) {
      accountId
      name
      secondaryContact {
        name
        firstName
      }
      primaryContact {
        name
        email
        userId
        firstName
      }
      technicianGroups {
        name
        groupId
      }
    }
  }
`;
const UPDATE_CLIENT = gql`
  mutation(
    $accountId: ID
    $name: String
    $primaryContact: UserInput
    $secondaryContact: UserInput
    $emailDomainList: [String]
    $technicianGroups: [GroupInput]
  ) {
    updateClient(
      input: {
        accountId: $accountId
        name: $name
        primaryContact: $primaryContact
        secondaryContact: $secondaryContact
        emailDomainList: $emailDomainList
        technicianGroups: $technicianGroups
      }
    ) {
      accountId
      name
    }
  }
`;

const DELETE_CLIENT = gql`
  mutation($accountId: ID!) {
    deleteClient(accountId: $accountId)
  }
`;

const CREATE_CLIENT_USER = gql`
  mutation(
    $firstName: String
    $lastName: String
    $email: String
    $sendActivationMail: Boolean
    $contactNumber: String
    $countryCode: String
    $reportingManager: UserInput
    $role: RoleInput
    $customFields: JSON
    $addAssociations: [CreateUserAssociationInput]
    $updateAssociations: [UpdateUserAssociationInput]
    $deleteAssociations: [UserAssociationIdentifierInput]
  ) {
    createClientUser(
      input: {
        firstName: $firstName
        lastName: $lastName
        email: $email
        sendActivationMail: $sendActivationMail
        contactNumber: $contactNumber
        countryCode: $countryCode
        reportingManager: $reportingManager
        role: $role
        customFields: $customFields
        addAssociations: $addAssociations
        updateAssociations: $updateAssociations
        deleteAssociations: $deleteAssociations
      }
    ) {
      userId
      firstName
      lastName
      email
      phoneNumber
      contactNumber
      countryCode
      loginRequired
      contactType
      activated
      activationMailStatus
      activationMailFailureReason
      role {
        roleId
        name
      }
      reportingManager {
        userId
        firstName
        lastName
        email
      }
      associations {
        client {
          accountId
          name
        }
        site {
          id
          name
        }
      }
      customFields
    }
  }
`;

const UPDATE_CLIENT_USER = gql`
  mutation(
    $userId: ID
    $firstName: String
    $lastName: String
    $email: String
    $contactNumber: String
    $countryCode: String
    $reportingManager: UserInput
    $role: RoleInput
    $customFields: JSON
    $addAssociations: [CreateUserAssociationInput]
    $updateAssociations: [UpdateUserAssociationInput]
    $deleteAssociations: [UserAssociationIdentifierInput]
  ) {
    updateClientUser(
      input: {
        userId: $userId
        firstName: $firstName
        lastName: $lastName
        email: $email
        contactNumber: $contactNumber
        countryCode: $countryCode
        reportingManager: $reportingManager
        role: $role
        customFields: $customFields
        addAssociations: $addAssociations
        updateAssociations: $updateAssociations
        deleteAssociations: $deleteAssociations
      }
    ) {
      userId
      firstName
      lastName
      email
      phoneNumber
      contactNumber
      countryCode
      loginRequired
      contactType
      activated
      activationMailStatus
      activationMailFailureReason
      role {
        roleId
        name
      }
      reportingManager {
        userId
        firstName
        lastName
        email
      }
      associations {
        client {
          accountId
          name
        }
        site {
          id
          name
        }
      }
      customFields
    }
  }
`;

const DELETE_CLIENT_USER = gql`
  mutation($userId: ID!) {
    deleteClient(userId: $userId)
  }
`;

const CREATE_CLIENT_CONVERSATION = gql`
  mutation(
    $clientId: ID!
    $content: String
    $subject: String
    $toUsers: [UserInput]
    $ccUsers: [UserInput]
    $bccUsers: [UserInput]
    $parentConversationId: ID
    $attachments: [AttachmentInput]
  ) {
    createClientConversation(
      clientId: $clientId
      input: {
        content: $content
        toUsers: $toUsers
        ccUsers: $ccUsers
        bccUsers: $bccUsers
        subject: $subject
        parentConversationId: $parentConversationId
        attachments: $attachments
      }
    ) {
      content
      time
      user {
        userId
        firstName
        lastName
        email
      }
      attachments {
        fileName
        originalFileName
        fileSize
      }
      subject
      parentConversationId
      conversationId
    }
  }
`;

const CREATE_CLIENT_OFFERINGS = gql`
  mutation($addedOfferings: [ClientOfferingInput!]!) {
    createClientOfferings(input: $addedOfferings) {
      offeringId
      name
      comments
      type
      price
    }
  }
`;

const DELETE_CLIENT_OFFERINGS = gql`
  mutation($deletedOfferings: [ID!]!) {
    deleteClientOfferings(ids: $deletedOfferings)
  }
`;

const UPDATE_CLIENT_OFFERINGS = gql`
  mutation($updatedOfferings: [ClientOfferingInput!]!) {
    updateClientOfferings(input: $updatedOfferings) {
      offeringId
      name
      comments
      type
      price
    }
  }
`;

const CREATE_WORKLOG = gql`
  mutation($ticketId: ID, $taskId: ID, $worklog: WorklogInput!) {
    createWorklog(ticketId: $ticketId, taskId: $taskId, input: $worklog) {
      worklogId
      technician {
        name
        userId
      }
      notes
      createdTime
      worklogItems {
        itemId
        serviceItem {
          name
        }
        billable
        quantity
      }
    }
  }
`;

const CREATE_CONTRACT = gql`
  mutation($clientId: ID!, $clientContract: ClientContractInput!) {
    createClientContract(clientId: $clientId, input: $clientContract) {
      contractId
      startDate
      endDate
      pricingType
      price
      blockHoursType
      blockHours
      overriddenOfferings {
        offering {
          offeringId
          name
        }
        price
      }
      blockedOfferings {
        offeringId
        name
      }
      active
      createdTime
    }
  }
`;

const UPDATE_CONTRACT = gql`
  mutation($contractId: ID!, $clientContract: ClientContractInput!) {
    updateClientContract(contractId: $contractId, input: $clientContract) {
      contractId
      startDate
      endDate
      pricingType
      price
      blockHoursType
      blockHours
      subscriptions {
        offering {
          offeringId
          name
        }
        quantity
      }
      overriddenOfferings {
        offering {
          offeringId
          name
        }
        price
      }
      blockedOfferings {
        offeringId
        name
      }
      sla {
        id
        slaOrder
        sla {
          id
          name
        }
      }
      active
      createdTime
    }
  }
`;

const CREATE_INVOICE = gql`
  mutation($clientId: ID!, $clientInvoice: ManualInvoiceInput!) {
    createInvoice(clientId: $clientId, input: $clientInvoice) {
      invoiceId
      displayId
      totalAmount
      invoiceDate
      dueDate
      items {
        itemId
        itemName
        quantity
        unitPrice
        amount
        worklogItem {
          itemId
        }
        offering {
          offeringId
        }
      }
      viewedByClient
    }
  }
`;

const CREATE_INVOICE_NEW = gql`
  mutation($clientId: ID!, $siteId: ID) {
    generateInvoice(clientId: $clientId, siteId: $siteId) {
      invoiceId
      displayId
      invoiceDate
      dueDate
      status
      discountAmount
      taxDetails
      taxRate
      taxAmount
      totalAmount
    }
  }
`;

const UPDATE_INVOICE_SETTING = gql`
  mutation($clientId: ID!, $setting: InvoiceSettingInput) {
    updateInvoiceSetting(clientId: $clientId, setting: $setting) {
      invoiceType
      scheduleDate
      frequency
      paymentDueDays
      sendEmail
      ticketStatus
      enabled
    }
  }
`;

const UPDATE_CLIENT_INVOICE_SETTING = gql`
  mutation($clientId: ID!, $setting: InvoiceSettingInput!) {
    updateInvoiceSetting(clientId: $clientId, setting: $setting) {
      invoiceSchedule
      invoiceDueDays
      ticketLineItemContent
      ticketLineItemPlaceholdersUsed
      projectLineItemContent
      projectLineItemPlaceholdersUsed
      autoPushInvoice
      generateSiteSpecific
      taxExempted
      salesTax {
        taxId
        name
      }
    }
  }
`;

const UPDATE_CLIENT_INVOICE_SETTING_NEW = gql`
  mutation($input: UpdateInvoiceSettingInput!) {
    updateInvoiceSettings(input: $input) {
      settingId
      invoiceSchedule
      scheduleEnabled
      overriddenSetting
      autoApplyCredits
      ticketLineItemContent
      ticketLineItemPlaceholdersUsed
      projectLineItemContent
      projectLineItemPlaceholdersUsed
      subscriptionLineItemContent
      subscriptionLineItemPlaceholdersUsed
      autoPushInvoice
      generateSiteSpecific
      taxExempted
      allowEmptyInvoice
      autoSendInvoice
      ignoreConsumedItem
      salesTax {
        taxId
        name
      }
      nextScheduleAt
      lastScheduleAt
      attachPdf
    }
  }
`;

const ORGANIZE_SLA = gql`
  mutation($clientSLAInput: [ClientSLAInput]!) {
    organizeClientSLA(input: $clientSLAInput)
  }
`;

const RESEND_USER_ACTIVATION = gql`
  mutation($userId: ID!) {
    resendUserActivationMail(userId: $userId)
  }
`;

const RESEND_USERS_ACTIVATION = gql`
  mutation resendUsersActivationMail($input: [ID!]!) {
    resendUsersActivationMail(input: $input) {
      userId
      activationMailStatus
      activationMailFailureReason
    }
  }
`;

const SOFT_DELETE_CLIENTS = gql`
  mutation($ids: [ID]!) {
    softDeleteClients(ids: $ids)
  }
`;

const HARD_DELETE_CLIENTS = gql`
  mutation($ids: [ID]!) {
    hardDeleteClients(ids: $ids)
  }
`;

const RESTORE_CLIENTS = gql`
  mutation($ids: [ID]!) {
    restoreClients(ids: $ids)
  }
`;

const UPDATE_READ_STATUS = gql`
  mutation($conversationId: ID!, $status: Boolean) {
    updateReadStatus(conversationId: $conversationId, status: $status)
  }
`;

const CREATE_INVOICE_FROM_BILLING_QUEUE = gql`
  mutation($input: CreateInvoicesFromBillingQueueInput!) {
    createInvoicesFromBillingQueue(input: $input)
  }
`;

const INVOICE_OFFERED_ITEMS = gql`
  mutation($input: InvoiceOfferedItemsInput!) {
    invoiceOfferedItems(input: $input)
  }
`;

const UPDATE_CLIENT_INVOICE_CONFIGURATION = gql`
  mutation updateInvoiceConfiguration($input: InvoiceConfigurationInput!) {
    updateInvoiceConfiguration(input: $input) {
      reminderEnabled
      reminders {
        reminderId
        reminderConfig {
          triggerType
          offsetValue
          offsetUnit
          referenceDateType
        }
        reminderTemplate {
          id
          name
          subject
          content
          placeholdersUsed
          to {
            type
            email
            group {
              groupId
              name
              description
            }
            user {
              userId
              email
              name
              firstName
              lastName
            }
            userTypeRole {
              roleId
              name
            }
            placeHolder
          }
          cc {
            type
            user {
              userId
              email
              firstName
              lastName
              name
            }
            placeHolder
            email
          }
          bcc {
            type
            user {
              userId
              email
              firstName
              lastName
              name
            }
            placeHolder
            email
          }
          activated
          templateSubtype
          attachmentList {
            fileName
            originalFileName
            fileSize
          }
        }
      }
      configurationId
      autoSyncWithTP
      enableSiteSpecificInvoice
      generateEmptyInvoice
      autoSend
      attachPdf
      attachUsagePdf
      autoApplyCredits
      invoiceSchedulesAllowed
      invoicePaymentTerm {
        paymentTermId
        paymentTermName
        paymentTermValue
        placeholder
        systemPaymentTerm
      }
      invoicePaymentMethod {
        paymentMethodId
        paymentMethodName
      }
      invoiceXmlFormat
      invoiceXmlEnabled
      taxable
      salesTax {
        taxId
        name
        rates {
          rateId
          name
          rateValue
        }
      }
      lateFee {
        lateFeeItem {
          itemId
          name
        }
        feeType
        value {
          model
          details {
            value
            afterHoursValue
          }
        }
        frequency
        gracePeriod
        taxable
        lateFeeEnabled
      }
      overriddenSettings
    }
  }
`;

export {
  GET_CLIENTS,
  UPDATE_CLIENT,
  CREATE_CLIENT,
  CREATE_CLIENT_WITH_POLICY,
  GET_CLIENT_DETAILS,
  DELETE_CLIENT,
  CREATE_CLIENT_USER,
  UPDATE_CLIENT_USER,
  DELETE_CLIENT_USER,
  CREATE_CLIENT_CONVERSATION,
  CREATE_CLIENT_OFFERINGS,
  DELETE_CLIENT_OFFERINGS,
  UPDATE_CLIENT_OFFERINGS,
  CREATE_WORKLOG,
  CREATE_CONTRACT,
  CREATE_INVOICE,
  UPDATE_CONTRACT,
  UPDATE_INVOICE_SETTING,
  ORGANIZE_SLA,
  RESEND_USER_ACTIVATION,
  SOFT_DELETE_CLIENTS,
  HARD_DELETE_CLIENTS,
  RESTORE_CLIENTS,
  CREATE_INVOICE_NEW,
  UPDATE_CLIENT_INVOICE_SETTING,
  UPDATE_CLIENT_INVOICE_SETTING_NEW,
  UPDATE_READ_STATUS,
  RESEND_USERS_ACTIVATION,
  CREATE_INVOICE_FROM_BILLING_QUEUE,
  INVOICE_OFFERED_ITEMS,
  GET_CLIENTS_FOR_POLICY_MIGRATION,
  GET_SITE_LIST,
  GET_CLIENTS_FOR_DROPDOWN,
  UPDATE_CLIENT_INVOICE_CONFIGURATION,
  GET_BASIC_CLIENT_DATA
};
